import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InputType } from '../../../constants/inputTypes'
import Icon from '../../ui/Icon'

const STRENGTH_RULES = [
  (v: string) => v.length >= 8,
  (v: string) => /[A-Z]/.test(v),
  (v: string) => /[a-z]/.test(v),
  (v: string) => /[0-9]/.test(v),
  (v: string) => /[^A-Za-z0-9]/.test(v),
]

const STRENGTH_LEVELS = [
  {
    highlight: 1,
    minRulesCheck: 1,
    translationKey: 'password-strength-weak',
  },
  {
    highlight: 2,
    minRulesCheck: 3,
    translationKey: 'password-strength-fair',
  },
  {
    highlight: 3,
    minRulesCheck: 4,
    translationKey: 'password-strength-good',
  },
  {
    highlight: 4,
    minRulesCheck: 5,
    translationKey: 'password-strength-strong',
  },
]
type StrengthLevel = (typeof STRENGTH_LEVELS)[number] | undefined

const findStrength = (password: string): StrengthLevel => {
  const checkedRules = STRENGTH_RULES.filter(rule => rule(password)).length
  for (let i = STRENGTH_LEVELS.length - 1; i >= 0; i--) {
    const strength = STRENGTH_LEVELS[i]
    if (checkedRules >= strength.minRulesCheck) return strength
  }
}

interface PasswordCreateInputProps {
  id?: string
  name: string
  value: string
  required?: boolean
  label: string
  confirmLabel: string
  placeHolder?: string
  confirmPlaceHolder?: string
  disabled?: boolean
  errorMessage?: string
  onChange: (name: string, value: string, isValid: boolean) => void
  onValidate?: (value: string) => boolean
}

export const PasswordCreateInput = ({
  id,
  name,
  value,
  required = true,
  label,
  confirmLabel,
  placeHolder = '',
  confirmPlaceHolder = '',
  disabled = false,
  errorMessage,
  onChange,
  onValidate,
}: PasswordCreateInputProps) => {
  const { t } = useTranslation('register')

  const [isError, setIsError] = useState(false)
  const [isTouch, setIsTouch] = useState(false)
  const [isFocus, setIsFocus] = useState(false)
  const [isShow, setIsShow] = useState(false)
  const [confirmValue, setConfirmValue] = useState('')
  const [isFocusConfirm, setIsFocusConfirm] = useState(false)
  const [isTouchConfirm, setIsTouchConfirm] = useState(false)

  const isRequired = isTouch && required && !value.trim()
  const passwordsMatch = value === confirmValue
  const strength = findStrength(value)
  const requirements = [
    { key: 'password-requirement-length', isMet: value.length >= 8 },
    { key: 'password-requirement-uppercase', isMet: /[A-Z]/.test(value) },
    { key: 'password-requirement-lowercase', isMet: /[a-z]/.test(value) },
    { key: 'password-requirement-number', isMet: /[0-9]/.test(value) },
    { key: 'password-requirement-special', isMet: /[^A-Za-z0-9]/.test(value) },
  ]
  const missingRequirements = requirements.filter(
    requirement => !requirement.isMet
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsTouch(true)
      const currentValue = e.target.value
      const hasValue = currentValue.trim().length > 0
      const nativeValid = e.target.validity.valid
      const customValid = onValidate ? onValidate(currentValue) : true
      const strengthValid = STRENGTH_RULES.every(rule => rule(currentValue))
      const matchValues = currentValue === confirmValue
      const valid = hasValue && nativeValid && customValid && strengthValid
      setIsError(hasValue && !valid)
      onChange(name, currentValue, valid && matchValues)
    },
    [onChange, name, onValidate, confirmValue]
  )

  const handleConfirmChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const currentValueConfirm = e.target.value
      const matchValues = value === currentValueConfirm
      const valid = !isError && matchValues
      setConfirmValue(currentValueConfirm)
      setIsTouchConfirm(true)
      onChange(name, value, valid)
    },
    [onChange, name, value, isError]
  )

  const fieldClass = [
    'field',
    required && 'required',
    !isFocus && isRequired && 'is-required',
    !isFocus && isError && 'is-error',
  ]
    .filter(Boolean)
    .join(' ')

  const confirmFieldClass = [
    'field',
    required && 'required',
    !isFocusConfirm && isTouchConfirm && !passwordsMatch && 'is-error',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <div className={fieldClass}>
        <label htmlFor={id}>{label}</label>
        <div className="input-icon-wrap">
          <input
            id={id}
            className="input"
            type={isShow ? InputType.Text : InputType.Password}
            placeholder={!disabled ? placeHolder : ''}
            required={required}
            disabled={disabled}
            name={name}
            value={value}
            onChange={handleChange}
            onFocus={() => setIsFocus(true)}
            onBlur={() => setIsFocus(false)}
          />
          <button
            type="button"
            onClick={() => setIsShow(v => !v)}
            className="input-icon-toggle"
            aria-label={t(isShow ? 'hide-password' : 'show-password')}
          >
            <Icon name={isShow ? 'eyeOff' : 'eye'} size={16} />
          </button>
        </div>
        {value && missingRequirements.length > 0 && (
          <div
            className="pw-requirements"
            aria-label={t('password-requirements')}
            aria-live="polite"
          >
            <span className="pw-requirements-label">
              {t('password-missing-requirements')}
            </span>
            <span className="pw-requirement">
              {missingRequirements
                .map(requirement => t(requirement.key))
                .join(' · ')}
            </span>
          </div>
        )}
        {strength && (
          <>
            <div className="pw-strength">
              {STRENGTH_LEVELS.map((level, index) => (
                <div
                  key={level.translationKey}
                  className={`pw-strength-bar${index < strength.highlight ? ` level-${strength.highlight}` : ''}`}
                />
              ))}
            </div>
            <div className="ph-row ph-between">
              <span className="pw-strength-label">
                {t('password-strength-label')}
              </span>
              <span className="pw-strength-label">
                {t(strength.translationKey)}
              </span>
            </div>
          </>
        )}
      </div>
      <div className={confirmFieldClass}>
        <label htmlFor={`${id}-confirm`}>{confirmLabel}</label>
        <div className="input-icon-wrap">
          <input
            id={`${id}-confirm`}
            className="input"
            type={InputType.Password}
            placeholder={!disabled ? confirmPlaceHolder : ''}
            required={required}
            disabled={disabled}
            name="confirmValue"
            value={confirmValue}
            onChange={handleConfirmChange}
            onFocus={() => setIsFocusConfirm(true)}
            onBlur={() => setIsFocusConfirm(false)}
          />
        </div>
        {isTouchConfirm && !isFocusConfirm && !passwordsMatch && (
          <div className="field-msg err" role="alert">
            <span className="ico">
              <Icon name="warn" size={13} />
            </span>
            {errorMessage}
          </div>
        )}
      </div>
    </>
  )
}
